import { captureException } from "@/monitoring";
import { impressionEventSchema } from "@/schemas";

import { impressionConfig } from "./config";
import { newEventId, resolvePosition } from "./utils";
import { stampEvent } from "./session";
import { enqueue } from "./queue";
import { isFlinging } from "./scroll";

import type { ImpressionEvent } from "@/schemas";
import type { CloseReason, Episode, TrackContext } from "./types";

// itemId -> open episode.
const episodes = new Map<string, Episode>();

const openEpisode = (ctx: TrackContext, now: number): void => {
  const position = resolvePosition(ctx.positionQueryKey, ctx.itemId);
  episodes.set(ctx.itemId, {
    ...ctx,
    openedAt: now,
    leftAt: now,
    position,
    // Viewport entry is itself an activity: open the first engaged window at `now`.
    engagedMs: 0,
    windowStart: now,
    windowEnd: now + impressionConfig.engagedWindowMs,
    readSample: false, // set by the first non-fling visible sample (see handleVisibilitySample)
    watchedMs: 0,
    soundOn: false,
    completed: false,
    stalled: false,
    backstopTimer: setTimeout(
      () => closeEpisode(ctx.itemId, "backstop"),
      impressionConfig.backstopMs,
    ),
  });
};

const closeEpisode = (itemId: string, reason: CloseReason): void => {
  const episode = episodes.get(itemId);
  if (!episode) return; // already closed (guards double-close: debounce vs unmount)

  clearTimeout(episode.closeTimer);
  clearTimeout(episode.backstopTimer);
  episode.videoCleanup?.(); // banks the final watch segment + detaches listeners
  episodes.delete(itemId);

  // Engaged dwell = banked windows + the open one, clamped to when the item left view.
  const endAt = reason === "viewport-exit" ? episode.leftAt : Date.now();
  const dwellMs =
    episode.engagedMs +
    Math.max(0, Math.min(episode.windowEnd, endAt) - episode.windowStart);

  // A too-short / idle view emits nothing. Video views need a higher floor — a 1–2s glance at a
  // video isn't a "view".
  const minDwell = episode.isVideo
    ? impressionConfig.minVideoDwellMs
    : impressionConfig.minDwellMs;
  if (dwellMs < minDwell) return;

  // Fling gate: drop a post that was only ever on screen while scrolling past it (never settled
  // on / read). The post-scroll sweep credits `readSample` for anything the user stopped on.
  if (impressionConfig.flingGateEnabled && !episode.readSample) return;

  // session_id + client_seq + first_impression are allocated together (the session may rotate
  // here on the idle boundary), so the seq/seen belong to the right session.
  const { sessionId, clientSeq, firstImpression } = stampEvent(episode.statusId);

  const event: ImpressionEvent = {
    event_id: newEventId(),
    event_type: "impression",
    event_time: new Date(episode.openedAt).toISOString(),
    status_id: episode.statusId,
    session_id: sessionId,
    client_seq: clientSeq,
    first_impression: firstImpression,
    is_reblog: episode.isReblog,
    is_quote: episode.isQuote,
    dwell_ms: dwellMs,
    surface: episode.surface,
    device: "web",
    app_version: impressionConfig.appVersion,
  };

  if (episode.authorAccountId) event.author_account_id = episode.authorAccountId; // omit if none
  if (episode.position !== null) event.position = episode.position; // omitted off-feed
  if (episode.signature) event.signature = episode.signature;
  if (episode.announcementId) event.announcement_id = episode.announcementId;
  if (episode.isVideo) {
    event.video = {
      watched_ms: Math.round(episode.watchedMs),
      completed: episode.completed,
      sound_on: episode.soundOn,
      playback_stalled: episode.stalled,
    };
  }

  // Validate before queueing so a malformed event is dropped, not shipped.
  const parsed = impressionEventSchema.safeParse(event);
  if (!parsed.success) {
    captureException(parsed.error);
    return;
  }
  enqueue(parsed.data);
};

/** Close every open episode — the "close" half of close-then-flush on unload. */
const closeAllEpisodes = (reason: CloseReason): void => {
  // Safe to delete the current key mid-iteration; closeEpisode only removes `itemId`.
  for (const itemId of episodes.keys()) closeEpisode(itemId, reason);
};

/**
 * Credit an activity at `now`: extend the open engaged window, or (if the idle gap exceeded
 * engagedWindowMs) bank the closed window and start a fresh one. Overlapping windows merge;
 * idle gaps are simply not carried into `engagedMs`.
 */
const markActivity = (episode: Episode, now: number): void => {
  if (now <= episode.windowEnd) {
    episode.windowEnd = now + impressionConfig.engagedWindowMs; // still engaged -> extend
  } else {
    episode.engagedMs += episode.windowEnd - episode.windowStart; // bank, exclude the gap
    episode.windowStart = now;
    episode.windowEnd = now + impressionConfig.engagedWindowMs;
  }
};

/**
 * Wire the native <video> in the status subtree to the episode's player-state accumulators.
 * Video posts only; a no-op if already attached or no <video> is present yet (retried on later
 * samples, since media can mount after the row). `timeupdate` also feeds engaged dwell — an
 * actively-playing video is activity even without scrolling.
 */
const attachVideo = (episode: Episode, element: Element): void => {
  if (episode.videoEl) return;
  const v = element.querySelector("video");
  if (!v) return;
  episode.videoEl = v;

  const refreshSound = (): void => {
    if (!v.muted && v.volume > 0) episode.soundOn = true;
  };
  const bankWatched = (): void => {
    if (episode.playStartedAt !== undefined) {
      episode.watchedMs += Date.now() - episode.playStartedAt;
      episode.playStartedAt = undefined;
    }
  };
  const onPlaying = (): void => {
    episode.playStartedAt = Date.now();
    refreshSound();
  };
  const onEnded = (): void => {
    bankWatched();
    episode.completed = true;
  };
  const onStall = (): void => {
    episode.stalled = true;
    bankWatched(); // buffering isn't watch time; resumes on the next `playing`
  };
  const onTimeUpdate = (): void => {
    refreshSound();
    markActivity(episode, Date.now());
    if (v.duration && v.currentTime >= v.duration - 0.25) episode.completed = true;
  };

  v.addEventListener("playing", onPlaying);
  v.addEventListener("pause", bankWatched);
  v.addEventListener("ended", onEnded);
  v.addEventListener("waiting", onStall);
  v.addEventListener("stalled", onStall);
  v.addEventListener("timeupdate", onTimeUpdate);
  v.addEventListener("volumechange", refreshSound);

  // Already playing when we attach (autoplay) -> start the watch clock now.
  if (!v.paused && !v.ended) onPlaying();

  episode.videoCleanup = (): void => {
    bankWatched();
    v.removeEventListener("playing", onPlaying);
    v.removeEventListener("pause", bankWatched);
    v.removeEventListener("ended", onEnded);
    v.removeEventListener("waiting", onStall);
    v.removeEventListener("stalled", onStall);
    v.removeEventListener("timeupdate", onTimeUpdate);
    v.removeEventListener("volumechange", refreshSound);
  };
};

/** Handle one visibility reading from the observer for a tracked element. */
const handleVisibilitySample = (
  ctx: TrackContext,
  isVisible: boolean,
  now: number,
  element: Element,
): void => {
  let episode = episodes.get(ctx.itemId);

  if (isVisible) {
    if (!episode) {
      openEpisode(ctx, now);
      episode = episodes.get(ctx.itemId)!;
    } else {
      if (episode.closeTimer) {
        // Re-entered within the debounce window -> same episode, cancel the pending close.
        clearTimeout(episode.closeTimer);
        episode.closeTimer = undefined;
      }
      markActivity(episode, now); // a visibility sample that includes the item is activity
    }
    // On screen while not flinging past it = an actual read; clears the fling gate.
    if (!isFlinging()) episode.readSample = true;
    if (episode.isVideo && !episode.videoEl) attachVideo(episode, element);
    return;
  }

  // Not visible: arm the flicker-debounce close (if one isn't already pending).
  if (episode && !episode.closeTimer) {
    episode.leftAt = now;
    episode.closeTimer = setTimeout(
      () => closeEpisode(ctx.itemId, "viewport-exit"),
      impressionConfig.flickerDebounceMs,
    );
  }
};

// Virtuoso usually unmounts the cell before the observer delivers the exit sample, so unmount
// is the real close trigger — no debounce.
const forceCloseOnUnmount = (itemId: string): void => {
  if (episodes.has(itemId)) closeEpisode(itemId, "unmount");
};

export { handleVisibilitySample, forceCloseOnUnmount, closeAllEpisodes };
